/**
 * What the Statements admin page (src/pages/StatementsAdmin.tsx) works out from the data:
 * who can be put on the pilot list, and what a rollout setting means in words.
 */
import type { EmployeeRow } from '@/data/types'
import type { RolloutSettings } from './send/rollout'

export interface Candidate {
  fccKey: string
  name: string
  department: string
  jobTitle: string
  /** Supervises someone in the data, or is management: the pilot is meant for them. */
  lead: boolean
}

const key = (s: string) => s.trim().toLowerCase()

/**
 * Everyone with an FCC key (the sender matches statements by it), leads first, then by
 * name. A person listed twice in the data (two departments in the period) appears once.
 */
export function pilotCandidates(employees: readonly EmployeeRow[]): Candidate[] {
  const supervising = new Set(employees.map((e) => e.supervisor).filter((s): s is string => !!s))
  const out = new Map<string, Candidate>()
  for (const e of employees) {
    if (!e.fccKey) continue
    const k = key(e.fccKey)
    const lead = e.management || supervising.has(e.employee)
    const had = out.get(k)
    if (had) {
      had.lead ||= lead
      continue
    }
    out.set(k, { fccKey: e.fccKey, name: e.employee, department: e.department, jobTitle: e.jobTitle, lead })
  }
  return [...out.values()].sort((a, b) => Number(b.lead) - Number(a.lead) || a.name.localeCompare(b.name))
}

/** The pilot list as people: a key not in the data is kept, by its key, so it can be removed. */
export function pilotPeople(
  pilot: readonly string[],
  candidates: readonly Candidate[],
): (Candidate | { fccKey: string; missing: true })[] {
  const byKey = new Map(candidates.map((c) => [key(c.fccKey), c]))
  return pilot.map((k) => byKey.get(key(k)) ?? { fccKey: k, missing: true as const })
}

/** One sentence: who the next send emails under these settings. */
export function rolloutSentence(s: RolloutSettings): string {
  if (s.pilotOnly) {
    const n = s.pilot.length
    return n === 0
      ? 'Nobody is emailed: the pilot list is empty.'
      : `Only the pilot list is emailed: ${n} ${n === 1 ? 'person' : 'people'}, their own statements.`
  }
  const leads = 'Every supervisor and manager is emailed their team and department statements'
  if (s.employees === 'all') return `${leads}, and every employee their own.`
  if (s.employees === 'departments') {
    const d = s.departments
    return d.length === 0
      ? `${leads}; no employee statements (no department picked).`
      : `${leads}, and employees in ${d.join(', ')} their own.`
  }
  return `${leads}; no employee statements.`
}

/** A hold reason (src/statements/types.ts HoldReason) in words; an unknown code as it is. */
const REASON: Record<string, string> = {
  claimVolume: 'claim-volume check',
  payrollMissing: 'payroll missing',
  noSupervisor: 'no supervisor',
  managementNoTeam: 'management with no team',
  noEmail: 'no email address',
}
export const reasonText = (codes: readonly string[] | string | null): string =>
  (typeof codes === 'string' ? codes.split(',') : (codes ?? []))
    .map((c) => c.trim())
    .filter(Boolean)
    .map((c) => REASON[c] ?? c)
    .join(', ')

export interface ReviewRow {
  employee: string
  reasons: string[]
  /** What happens to them: a statement waiting for approval, one approved, or none at all. */
  statement: 'held' | 'approved' | 'none'
}

/**
 * The run's review list against its statement rows. A person with no held or approved row
 * gets no statement this month (management with no team): shown, nothing to approve.
 */
export function reviewRows(
  review: readonly { employee: string; reasons: string[] }[],
  statements: readonly { employee: string; status: string; approvedBy: string | null }[],
): ReviewRow[] {
  return review.map((p) => {
    const rows = statements.filter((s) => s.employee === p.employee)
    const statement = rows.some((s) => s.status === 'held') ? 'held' : rows.some((s) => s.approvedBy) ? 'approved' : 'none'
    return { employee: p.employee, reasons: p.reasons, statement }
  })
}

export interface SendLine {
  status: string
  sends: { outcome: 'sent' | 'failed' }[]
  opened: unknown | null
}

/**
 * Where one statement's email stands: sent once any attempt went; failed when it was tried
 * and none went; otherwise not yet sent. Held, withdrawn and the like are not sent either.
 */
export function sendState(s: SendLine): 'sent' | 'failed' | 'notSent' {
  if (s.sends.some((x) => x.outcome === 'sent')) return 'sent'
  return s.sends.length ? 'failed' : 'notSent'
}

/** The counts above the Sends table. Opened: the recipient opened or downloaded it in the app. */
export function sendsSummary(rows: readonly SendLine[]): { sent: number; failed: number; notSent: number; opened: number } {
  const out = { sent: 0, failed: 0, notSent: 0, opened: 0 }
  for (const r of rows) {
    out[sendState(r)]++
    if (r.opened) out.opened++
  }
  return out
}

/** Settings equal for saving purposes (order of the lists aside). */
export function sameSettings(a: RolloutSettings, b: RolloutSettings): boolean {
  const set = (xs: string[]) => [...xs].map(key).sort().join('\n')
  return (
    a.pilotOnly === b.pilotOnly &&
    a.employees === b.employees &&
    set(a.pilot) === set(b.pilot) &&
    set(a.departments) === set(b.departments)
  )
}
